import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketLogsQuery, type TicketLogEntry, type TicketLogsPage } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import './TicketLogs.css'

/**
 * Fila "Logs" - `easeedesk/tickets/details/logs.php`.
 *
 * Doua surse puse la un loc, fiindca intrebarea e una singura: ce s-a intamplat
 * cu masina asta, in ordinea in care s-a intamplat. Un driver schimbat la 14:02
 * si o eroare de disc la 14:05 spun impreuna altceva decat fiecare in parte.
 *
 * Filtrarea pe masina ASCUNDE randuri, nu cere altele: datele sunt toate aici,
 * iar un drum in plus pentru o filtrare ar fi doar asteptare. Schimbarea
 * perioadei, in schimb, reincarca fila intreaga - si randul de sus, care spune
 * pe ce masini s-a uitat, se socoteste din aceleasi date.
 *
 * Lista vine intreaga de la API, dar se arata cate STEP randuri o data, cu un
 * "Show more" dedesubt: pe un tichet cu 90 de zile de syslog fila se intindea
 * cat tot ecranul si ingropa restul tichetului. Taierea e pe client - datele
 * sunt deja aici - si o ia de la capat cand se schimba masina sau perioada.
 */

const STEP = 15

const KINDS = {
  change: { className: 'lg-change', label: 'Change' },
  log: { className: 'lg-log', label: 'Critical' },
  syslog: { className: 'lg-syslog', label: 'Syslog' },
}

export function TicketLogs({ id }: { id: number }) {
  const [days, setDays] = useState(30)
  const [group, setGroup] = useState('')
  const { data, error } = useQuery(ticketLogsQuery(id, days))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const shown = group === '' ? data.entries : data.entries.filter((e) => e.group === group)

  return (
    <div id={`lgTab${id}`}>
      <div className="lg-bar">
        <select
          className="form-control input-sm lg-days"
          value={days}
          onChange={(ev) => {
            setDays(Number(ev.target.value))
            setGroup('')
          }}
        >
          {data.periods.map((p) => (
            <option key={p.days} value={p.days}>
              {p.label}
            </option>
          ))}
        </select>

        <span className="lg-muted" style={{ fontSize: 12 }}>
          {/*
           * Se spune pe ce masini s-a uitat - un ecran gol pe un tichet fara
           * echipamente arata la fel cu unul pe o masina linistita, si sunt doua
           * lucruri cu totul diferite. Doar cand NU sunt butoane: cu mai multe
           * masini, insiruirea de nume spunea acelasi lucru ca butoanele de
           * dedesubt, doar ca necitibil.
           */}
          {data.devices.length === 0 && 'No device is attached to this ticket.'}
          {data.devices.length === 1 && data.devices[0].name}
        </span>
      </div>

      {data.devices.length > 1 && (
        <ul className="nav nav-tabs nav-tabs-secondary lg-devs" role="tablist">
          <li role="presentation" className={group === '' ? 'active' : undefined}>
            <a data-group="" onClick={() => setGroup('')}>
              <i className="fas fa-layer-group"></i> All <span className="lg-n">({data.entries.length})</span>
            </a>
          </li>
          {data.devices.map((device) => (
            <li key={device.key} role="presentation" className={group === device.key ? 'active' : undefined}>
              <a data-group={device.key} onClick={() => setGroup(device.key)}>
                <i className="fas fa-desktop"></i> {device.name} <span className="lg-n">({device.count})</span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {data.entries.length === 0 ? <Nothing page={data} /> : <Table key={`${days}/${group}`} entries={shown} />}
    </div>
  )
}

function Nothing({ page }: { page: TicketLogsPage }) {
  return (
    <div className="lg-none">
      <i className="fas fa-info-circle"></i>{' '}
      {page.devices.length === 0
        ? 'Attach a device or a configuration item to the ticket, and its changes, critical logs and syslog show up here.'
        : 'Nothing was recorded on these devices in the period.'}
    </div>
  )
}

function Table({ entries }: { entries: TicketLogEntry[] }) {
  const [limit, setLimit] = useState(STEP)
  const visible = entries.slice(0, limit)
  const left = entries.length - visible.length
  return (
    <>
      <table className="table table-striped table-bordered lg-table" width="100%">
        <thead>
          <tr>
            <th style={{ width: 140 }}>When</th>
            <th style={{ width: 90 }}>Kind</th>
            <th style={{ width: 150 }}>Device</th>
            <th>What</th>
            <th style={{ width: 160 }}>Authorised</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((entry, i) => {
            const kind = KINDS[entry.kind] ?? KINDS.log
            return (
              <tr key={i} data-group={entry.group}>
                <td className="lg-muted text-nowrap">{entry.at}</td>
                <td>
                  <span className={`lg-kind ${kind.className}`}>{kind.label}</span>
                </td>
                <td className="lg-dev">{entry.device}</td>
                <td>{entry.kind === 'change' ? <What entry={entry} /> : <Said entry={entry} />}</td>
                <td>
                  <Authorised entry={entry} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <p className="lg-foot lg-muted" style={{ fontSize: 12 }}>
        {left > 0 ? (
          <>
            <span className="lg-shown">{visible.length}</span> of <span className="lg-count">{entries.length}</span> entries.{' '}
            <button type="button" className="btn btn-default btn-xs lg-more" onClick={() => setLimit((n) => n + STEP)}>
              Show {Math.min(STEP, left)} more
            </button>
          </>
        ) : (
          <>
            <span className="lg-count">{entries.length}</span> entries.
          </>
        )}
      </p>
    </>
  )
}

function What({ entry }: { entry: TicketLogEntry }) {
  return (
    <>
      <span className="lg-muted">
        {entry.type}
        {entry.field ? <> &middot; {entry.field}</> : null}
      </span>
      <br />
      {entry.value}
    </>
  )
}

function Said({ entry }: { entry: TicketLogEntry }) {
  return (
    <>
      <span className="lg-muted">
        {entry.category}
        {entry.object ? <> &middot; {entry.object}</> : null}
      </span>
      <br />
      <strong>{entry.title}</strong>
      {entry.message ? (
        <div className="lg-muted" style={{ marginTop: 2 }}>
          {entry.message}
        </div>
      ) : null}
    </>
  )
}

/**
 * Coloana e a SCHIMBARILOR, nu a jurnalelor: un mesaj de eroare nu e ceva ce
 * cineva aproba. Lasata cu o linie pe ele, in loc sa scrie "nu" - un "nu" acolo
 * ar citi ca o neregula.
 */
function Authorised({ entry }: { entry: TicketLogEntry }) {
  if (entry.kind !== 'change') return <span className="lg-muted">&mdash;</span>

  if (!entry.authorized) return <span className="label label-warning">no</span>

  return (
    <>
      <span className="label label-success">yes</span>{' '}
      <span className="lg-muted">{entry.window ? entry.window : entry.ticketId ? `ticket #${entry.ticketId}` : ''}</span>
    </>
  )
}
